import { ReactElement, ReactNode, useEffect, useState } from "react";
import { Checkbox, Field, Label, MediaInput } from "@zendeskgarden/react-forms";
import { ReactComponent as SearchIcon } from '@zendeskgarden/svg-icons/src/16/search-stroke.svg';
import { IconButton } from "@zendeskgarden/react-buttons";
import { GrRedo, GrUndo } from "react-icons/gr";
import BarLoader from "../../bar-loader/BarLoader";
import { Tooltip } from "@zendeskgarden/react-tooltips";
import { Close } from "@material-ui/icons";


export interface IVZHotTableControlsProps {
    canUndo: boolean;
    canRedo: boolean;
    isSearchable: boolean;
    isUndoRedoable: boolean;
    canSuppressZeroes: boolean;
    isDataLoading: boolean;
    onUndo?: () => void;
    onRedo?: () => void;
    onFilterTextChange: (text: string) => void;
    onSuppressZeroes: (suppressZeroes: boolean) => void;
    customControls?: ReactNode;
}

export default function VZHotTableControls({
    canUndo,
    canRedo,
    isSearchable,
    isUndoRedoable,
    canSuppressZeroes,
    isDataLoading,
    onUndo,
    onRedo,
    onFilterTextChange,
    onSuppressZeroes,
    customControls,
}: IVZHotTableControlsProps): ReactElement {
    // https://developer.mozilla.org/en-US/docs/Web/API/Navigator/platform
    const modifierKeyPrefix = navigator.platform.startsWith("Mac") || navigator.platform === "iPhone" ? "⌘" : "Ctrl+";
    const [filterText, setFilterText] = useState("");

    useEffect(() => {
        onFilterTextChange(filterText);
    }, [filterText]);

    return (
        <div className="vz-hot-table-controls">
            { isSearchable &&
                <Field className={`filter-field ${filterText.length > 0 && "is-clearable"}`}>
                    <MediaInput
                        start={<SearchIcon />}
                        placeholder="Search table"
                        value={filterText}
                        onChange={(evt) => {
                            setFilterText(evt.target.value);
                        }}
                        aria-label="search table"
                        end={<IconButton size="small" onClick={() => setFilterText("")}><Close /></IconButton>}
                    />
                </Field>
            }
            { isUndoRedoable &&
                <>
                    <span className="vz-hot-table-controls-divider"></span>
                    <Tooltip content={`Undo (${modifierKeyPrefix}Z)`} delayMS={0} hasArrow={false} placement={"bottom"} zIndex={110}>
                        <IconButton
                            isBasic={false}
                            isPill={false}
                            isPrimary={!canUndo}
                            aria-label="square undo"
                            disabled={!canUndo}
                            onClick={onUndo}
                        >
                            <GrUndo />
                        </IconButton>
                    </Tooltip>
                    <Tooltip content={`Redo (${modifierKeyPrefix}Y)`} delayMS={0} hasArrow={false} placement={"bottom"} zIndex={110}>
                        <IconButton
                            isBasic={false}
                            isPill={false}
                            isPrimary={!canRedo}
                            aria-label="square redo"
                            disabled={!canRedo}
                            onClick={onRedo}
                        >
                            <GrRedo />
                        </IconButton>
                    </Tooltip>
                </>
            }

            <div style={{marginLeft: "auto"}}></div>

            {customControls}

            { canSuppressZeroes &&
                <Field>
                    <Checkbox onChange={(evt) => onSuppressZeroes(evt.target.checked)}>
                        <Label>Suppress Zeroes</Label>
                    </Checkbox>
                </Field>
            }

            <BarLoader loading={isDataLoading} position="bottom" />
        </div>
    );
}
